// based on https://stackoverflow.com/a/35354087

export const resizePath = (path: SVGPathElement, width: number, height: number) => {
  // const obj = getTranslationAndScaling(path, width, height);
  const oldPathDStr = path.getAttributeNS(null, 'd') || '';
  const originalSize = path.getBBox({ fill: true, stroke: false });

  const scaleX = width / originalSize.width;
  const scaleY = height / originalSize.height;
  const pathTranslX = 0; //obj.pathTranslX;
  const pathTranslY = 0; //obj.pathTranslY;
  // const scale       = obj.scale;

  const newPathDStr = getTransformedPathDStr(oldPathDStr, pathTranslX, pathTranslY, scaleX, scaleY);
  return newPathDStr;
};

const getTransformedPathDStr = (
  oldPathDStr: string,
  pathTranslX: number,
  pathTranslY: number,
  scaleX: number,
  scaleY: number,
) => {
  // constants to help keep track of the types of SVG commands in the path
  const BOTH_X_AND_Y = 1;
  const JUST_X = 2;
  const JUST_Y = 3;
  const NONE = 4;
  const ELLIPTICAL_ARC = 5;
  const ABSOLUTE = 6;
  const RELATIVE = 7;

  // two parallel arrays, with each element being one component of the
  // "d" attribute of the SVG path, with one component being either
  // an instruction (e.g. "M" for moveto, etc.) or numerical value
  // for either an x or y coordinate
  const oldPathDArr = getArrayOfPathDComponents(oldPathDStr);
  const newPathDArr = [];

  let commandParams;
  let absOrRel;
  let oldPathDComp;

  // element index
  let idx = 0;

  while (idx < oldPathDArr.length) {
    oldPathDComp = oldPathDArr[idx] ?? '';
    if (/^[A-Za-z]$/.test(oldPathDComp)) {
      // component is a single letter, i.e. an svg path command
      newPathDArr[idx] = oldPathDArr[idx];
      switch (oldPathDComp.toUpperCase()) {
        case 'A': // elliptical arc command...the most complicated one
          commandParams = ELLIPTICAL_ARC;
          break;
        case 'H': // horizontal line; requires only an x-coordinate
          commandParams = JUST_X;
          break;
        case 'V': // vertical line; requires only a y-coordinate
          commandParams = JUST_Y;
          break;
        case 'Z': // close the path
          commandParams = NONE;
          break;
        default:
          // all other commands; all of them require both x and y coordinates
          commandParams = BOTH_X_AND_Y;
      }
      absOrRel = oldPathDComp === oldPathDComp.toUpperCase() ? ABSOLUTE : RELATIVE;
      // lowercase commands are relative, uppercase are absolute
      idx += 1;
    } else {
      // if the component is not a letter, then it is a numeric value
      let translX = 0;
      let translY = 0;
      if (absOrRel === ABSOLUTE) {
        // the translation is required for absolute commands...
        translX = pathTranslX;
        translY = pathTranslY;
      } else if (absOrRel === RELATIVE) {
        // ...but not relative ones
        translX = 0;
        translY = 0;
      }
      // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
      switch (commandParams) {
        // figure out which of the numeric values following an svg command
        // are required, and then transform the numeric value(s) from the
        // original path d-attribute and place it in the same location in the
        // array that will eventually become the d-attribute for the new path
        case BOTH_X_AND_Y:
          newPathDArr[idx] = Number(oldPathDArr[idx]) * scaleX + translX;
          newPathDArr[idx + 1] = Number(oldPathDArr[idx + 1]) * scaleY + translY;
          idx += 2;
          break;
        case JUST_X:
          newPathDArr[idx] = Number(oldPathDArr[idx]) * scaleX + translX;
          idx += 1;
          break;
        case JUST_Y:
          newPathDArr[idx] = Number(oldPathDArr[idx]) * scaleY + translY;
          idx += 1;
          break;
        case ELLIPTICAL_ARC:
          // the elliptical arc has x and y values in the first and second as well as
          // the 6th and 7th positions following the command; the intervening values
          // are not affected by the transformation and so can simply be copied
          newPathDArr[idx] = Number(oldPathDArr[idx]) * scaleX + translX;
          newPathDArr[idx + 1] = Number(oldPathDArr[idx + 1]) * scaleY + translY;
          newPathDArr[idx + 2] = Number(oldPathDArr[idx + 2]);
          newPathDArr[idx + 3] = Number(oldPathDArr[idx + 3]);
          newPathDArr[idx + 4] = Number(oldPathDArr[idx + 4]);
          newPathDArr[idx + 5] = Number(oldPathDArr[idx + 5]) * scaleX + translX;
          newPathDArr[idx + 6] = Number(oldPathDArr[idx + 6]) * scaleY + translY;
          idx += 7;
          break;
        case NONE:
          throw new Error('numeric value should not follow the SVG Z/z command');
          break;
      }
    }
  }
  return newPathDArr.join(' ');
};

const getArrayOfPathDComponents = (str: string) => {
  // assuming the string from the d-attribute of the path has all components
  // separated by a single space, then create an array of components by
  // simply splitting the string at those spaces
  str = standardizePathDStrFormat(str);
  return str.split(' ').filter((x) => !!x.trim());
};

const standardizePathDStrFormat = (str: string) =>
  // The SVG standard is flexible with respect to how path d-strings are
  // formatted but this makes parsing them more difficult. This function ensures
  // that all SVG path d-string components (i.e. both commands and values) are
  // separated by a single space.
  str
    .replace(/,/g, ' ') // replace each comma with a space
    .replace(/-/g, ' -') // precede each minus sign with a space
    .replace(/([A-Za-z])/g, ' $1 ') // sandwich each   letter between 2 spaces
    .replace(/ {2}/g, ' ') // collapse repeated spaces to a single space
    .replace(/ ([Ee]) /g, '$1') // remove flanking spaces around exponent symbols
    .replace(/^ /g, '') // trim any leading space
    .replace(/ $/g, ''); // trim any tailing space
